import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { Button } from './Button';
import './ConfirmDialog.css';

/**
 * Fenêtre de confirmation (UI-18) : role alertdialog, piège de focus, Échap annule,
 * focus initial sur « Annuler » (choix sans risque), retour du focus à l'élément d'origine.
 */
export function ConfirmDialog({ open, title, description, confirmLabel = 'Confirmer', tone = 'danger', busy = false, onConfirm, onCancel }) {
  const rootRef = useRef(null);
  const cancelRef = useRef(null);
  const returnRef = useRef(null);
  const titleId = useId();
  const descId = useId();

  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useFocusTrap(rootRef, open);

  useEffect(() => {
    if (!open) return undefined;
    returnRef.current = document.activeElement;
    const frame = requestAnimationFrame(() => cancelRef.current?.focus());
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCancelRef.current();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown, true);
      const back = returnRef.current;
      requestAnimationFrame(() => {
        if (back?.isConnected) back.focus({ preventScroll: true });
      });
    };
  }, [open]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="confirm-layer">
          <motion.div
            className="confirm-overlay"
            aria-hidden="true"
            onClick={busy ? undefined : onCancel}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          />
          <motion.div
            ref={rootRef}
            className="confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={description ? descId : undefined}
            initial={{ opacity: 0, scale: 0.98, y: 6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0, 0, 0, 1] }}
          >
            <h2 id={titleId} className="confirm-dialog__title">{title}</h2>
            {description && <p id={descId} className="confirm-dialog__text">{description}</p>}
            <div className="confirm-dialog__actions">
              <Button ref={cancelRef} className="confirm-dialog__button" onClick={onCancel} disabled={busy}>
                Annuler
              </Button>
              <Button
                variant={tone === 'danger' ? 'danger' : 'primary'}
                className="confirm-dialog__button"
                onClick={onConfirm}
                loading={busy}
              >
                {confirmLabel}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
